import { useModal } from "@/features/modal/ModalStackManager";
import { Calendar, CalendarDate } from "@/features/ui/calendar";
import { Machine, MachineSearchBox } from "@/features/ui/PMS/machineSearchBox";
import { Button, Flex, Text } from "@mantine/core";
import { IconCalendar, IconReportSearch } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useState } from "react";
import DataGrid, { Column } from "react-data-grid";
import { AnalysisPressChart } from "../../../features/ui/PMS/chart/AnalysisPressChart";
import { machineItemMockupData } from "../output";

export const AnalysisAbilityPage = () => {
  const { openModal, closeModal } = useModal();
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [dates, setDates] = useState<CalendarDate>([null, null]);
  const [itemCode, setItemCode] = useState<string>("");

  const handleSelectMachine = (machine: Machine | null) => {
    if (machine) {
      setSelectedMachine(machine);
      closeModal("");
    }
  };

  const ch2Columns: readonly Column<any>[] = [
    {
      key: "ch2ton",
      name: "일 평균 ch2",
      sortable: true,
      resizable: true,
    },
  ];

  const ch1Columns: readonly Column<any>[] = [
    {
      key: "ch1ton",
      name: "일 평균 ch1",
      sortable: true,
      resizable: true,
    },
  ];

  const totalColumns: readonly Column<any>[] = [
    {
      key: "totalTon",
      name: "일 평균 토탈 톤",
      sortable: true,
      resizable: true,
    },
  ];

  const machineColumns: readonly Column<any>[] = [
    {
      key: "make",
      name: "기계 제조사",
      sortable: true,
      resizable: true,
    },
    {
      key: "name",
      name: "기계 이름",
      sortable: true,
      resizable: true,
    },
    {
      key: "createdAt",
      name: "제조 연월일",
      sortable: true,
      resizable: true,
    },
    {
      key: "code",
      name: "제조 번호",
      sortable: true,
      resizable: true,
    },
  ];

  const itemColumns: readonly Column<any>[] = [
    {
      key: "model",
      name: "모델",
      sortable: true,
      resizable: true,
    },
    {
      key: "code",
      name: "코드",
      sortable: true,
      resizable: true,
    },
    {
      key: "name",
      name: "품명",
      sortable: true,
      resizable: true,
    },

    {
      key: "spec",
      name: "규격",
      sortable: true,
      resizable: true,
    },
  ];

  return (
    <Flex gap="md" direction="column" p="md">
      <Flex justify="space-between">
        <Text size="xl">능력</Text>
        <Flex gap="md">
          <Button
            leftIcon={<IconCalendar />}
            color="lime"
            onClick={() => {
              openModal(<Calendar dates={[null, null]} type="default" />, null, "날짜 선택").then(
                (selectedDates) => {
                  if (selectedDates) {
                    setDates(selectedDates);
                  }
                },
              );
            }}
          >
            {`${dates[0] !== null ? dayjs(dates[0]).format("YYYY-MM-DD") : ""} 날짜선택`}
          </Button>
          <Button
            onClick={() => {
              openModal(<MachineSearchBox onSelect={handleSelectMachine} />, null, "기계 검색");
            }}
            leftIcon={<IconReportSearch />}
          >
            설비선택
          </Button>
        </Flex>
      </Flex>
      <DataGrid
        columns={machineColumns ?? []}
        rows={selectedMachine ? [selectedMachine] : []}
        rowHeight={40}
        rowKeyGetter={(row) => row.id}
        style={{ height: "120px", width: "100%" }}
      />
      <Flex gap="md">
        <DataGrid
          columns={itemColumns ?? []}
          rows={
            Array.isArray(machineItemMockupData[selectedMachine?.id ?? 0])
              ? machineItemMockupData[selectedMachine?.id ?? 0]
              : []
          }
          rowHeight={40}
          rowKeyGetter={(row) => row.code}
          onRowClick={(row) => {
            setItemCode(row.code);
          }}
          style={{ height: "100%", width: "30%" }}
        />
        <AnalysisPressChart itemCode={itemCode} mode="point" />
      </Flex>
      <Flex gap="md" w="100%">
        <DataGrid
          columns={ch1Columns ?? []}
          rows={
            itemCode && itemDetailMockupData[itemCode]?.ch1ton
              ? [{ ch1ton: itemDetailMockupData[itemCode].ch1ton }]
              : []
          }
          rowHeight={40}
          style={{ height: "80px", width: "33%" }}
        />
        <DataGrid
          columns={ch2Columns ?? []}
          rows={
            itemCode && itemDetailMockupData[itemCode]?.ch2ton
              ? [{ ch2ton: itemDetailMockupData[itemCode].ch2ton }]
              : []
          }
          rowHeight={40}
          style={{ height: "80px", width: "33%" }}
        />
        <DataGrid
          columns={totalColumns ?? []}
          rows={
            itemCode && itemDetailMockupData[itemCode]?.totalTon
              ? [{ totalTon: itemDetailMockupData[itemCode].totalTon }]
              : []
          }
          rowHeight={40}
          style={{ height: "100%", width: "33%" }}
        />
      </Flex>
    </Flex>
  );
};

const itemDetailMockupData: Record<
  string,
  {
    ch1ton: string;
    ch2ton: string;
    totalTon: string;
  }
> = {
  ITM001: {
    ch1ton: "10 ton",
    ch2ton: "5 ton",
    totalTon: "15 ton",
  },
  ITM002: {
    ch1ton: "20 ton",
    ch2ton: "10 ton",
    totalTon: "30 ton",
  },
  ITM003: {
    ch1ton: "30 ton",
    ch2ton: "15 ton",
    totalTon: "45 ton",
  },
  ITM004: {
    ch1ton: "40 ton",
    ch2ton: "20 ton",
    totalTon: "60 ton",
  },
  ITM005: {
    ch1ton: "50 ton",
    ch2ton: "25 ton",
    totalTon: "75 ton",
  },
  ITM006: {
    ch1ton: "60 ton",
    ch2ton: "30 ton",
    totalTon: "90 ton",
  },
  ITM007: {
    ch1ton: "70 ton",
    ch2ton: "35 ton",
    totalTon: "105 ton",
  },
  ITM008: {
    ch1ton: "80 ton",
    ch2ton: "40 ton",
    totalTon: "120 ton",
  },
  ITM009: {
    ch1ton: "90 ton",
    ch2ton: "45 ton",
    totalTon: "135 ton",
  },
  ITM010: {
    ch1ton: "100 ton",
    ch2ton: "50 ton",
    totalTon: "150 ton",
  },
  ITM011: {
    ch1ton: "110 ton",
    ch2ton: "55 ton",
    totalTon: "165 ton",
  },
  ITM012: {
    ch1ton: "120 ton",
    ch2ton: "60 ton",
    totalTon: "180 ton",
  },
  ITM013: {
    ch1ton: "130 ton",
    ch2ton: "65 ton",
    totalTon: "195 ton",
  },
  ITM014: {
    ch1ton: "140 ton",
    ch2ton: "70 ton",
    totalTon: "210 ton",
  },
  ITM015: {
    ch1ton: "150 ton",
    ch2ton: "75 ton",
    totalTon: "225 ton",
  },
  ITM016: {
    ch1ton: "160 ton",
    ch2ton: "80 ton",
    totalTon: "240 ton",
  },
  ITM017: {
    ch1ton: "170 ton",
    ch2ton: "85 ton",
    totalTon: "255 ton",
  },
  ITM018: {
    ch1ton: "180 ton",
    ch2ton: "90 ton",
    totalTon: "270 ton",
  },
  ITM019: {
    ch1ton: "190 ton",
    ch2ton: "95 ton",
    totalTon: "285 ton",
  },
  ITM020: {
    ch1ton: "200 ton",
    ch2ton: "100 ton",
    totalTon: "300 ton",
  },
};
